import { PROSPECT_IMPORT_FIELD_LABELS } from './prospectImportFields';

/** Regroupe colonnes CSV → champ prospect (plusieurs colonnes → concat avec espace). */
export function buildApiMappingFromColumns(columnToField) {
  const mapping = {};
  Object.entries(columnToField || {}).forEach(([col, field]) => {
    if (!field) return;
    const existing = mapping[field];
    if (!existing) {
      mapping[field] = col;
      return;
    }
    if (Array.isArray(existing)) {
      existing.push(col);
    } else {
      mapping[field] = [existing, col];
    }
  });
  return mapping;
}

function readMappedCell(row, csvColumnOrColumns) {
  const cols = Array.isArray(csvColumnOrColumns)
    ? csvColumnOrColumns
    : [csvColumnOrColumns];
  const parts = cols
    .map((col) => {
      const raw = row[col];
      return typeof raw === 'string' ? raw.trim() : raw != null ? String(raw).trim() : '';
    })
    .filter(Boolean);
  return parts.join(' ');
}

/** Valeur finale d'un champ (y compris fusion de plusieurs colonnes CSV). */
export function readImportFieldValue(row, mapping, prospectField) {
  const csvColumn = mapping?.[prospectField];
  if (!mappingHasCsvColumn(csvColumn)) return '';
  return readMappedCell(row, csvColumn);
}

export function isImportFieldValuePresent(value) {
  if (value == null) return false;
  return String(value).trim().length > 0;
}

const IMPORT_FIELDS = new Set([
  'fullName',
  'email',
  'phone',
  'company',
  'position',
  'mobilePhone',
  'priority',
  'status',
  'notes',
  'address',
  'postalCode',
  'city',
  'website',
  'ape',
  'siret',
  'tags',
  'callbackAt',
]);

function mappingHasCsvColumn(csvColumn) {
  if (csvColumn == null) return false;
  if (Array.isArray(csvColumn)) return csvColumn.length > 0;
  return String(csvColumn).trim().length > 0;
}

/** Champs marqués obligatoires et présents dans le mapping. */
export function normalizeRequiredImportFields(requiredFields, mapping) {
  const mapped = new Set(Object.keys(mapping || {}));
  const list = Array.isArray(requiredFields) ? requiredFields : [];
  return list.filter((f) => mapped.has(f) && IMPORT_FIELDS.has(f));
}

/** Applique le mapping API sur une ligne CSV (aperçu, même logique que le serveur). */
export function applyImportMappingToRow(row, mapping, tagsForAll = [], requiredFields = []) {
  const data = {};
  for (const [prospectField, csvColumn] of Object.entries(mapping || {})) {
    if (!IMPORT_FIELDS.has(prospectField)) continue;
    if (!mappingHasCsvColumn(csvColumn)) continue;
    data[prospectField] = readImportFieldValue(row, mapping, prospectField);
  }

  for (const prospectField of normalizeRequiredImportFields(requiredFields, mapping)) {
    if (isImportFieldValuePresent(data[prospectField])) continue;
    return {
      ok: false,
      reason: 'missing_mapped_field',
      field: prospectField,
      label: PROSPECT_IMPORT_FIELD_LABELS[prospectField] || prospectField,
    };
  }

  const phone = (data.phone ?? '').trim();
  const rawName = (data.fullName ?? '').trim();
  const rawCompany = (data.company ?? '').trim();
  const emailRow = (data.email ?? '').trim().toLowerCase();

  if (!phone && !emailRow && !rawName && !rawCompany) {
    return { ok: false, reason: 'missing_identity' };
  }

  const fullName = rawName || rawCompany || phone || emailRow || 'Sans nom';
  const companyStored = rawCompany || '';

  let tags = [];
  if (data.tags && typeof data.tags === 'string') {
    tags = data.tags
      .split(/[,;]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  const globalTags = Array.isArray(tagsForAll)
    ? tagsForAll.map((t) => String(t).trim()).filter(Boolean)
    : [];
  tags = Array.from(new Set([...tags, ...globalTags]));

  return {
    ok: true,
    prospect: {
      fullName,
      company: companyStored,
      position: data.position || '',
      phone,
      mobilePhone: data.mobilePhone || '',
      email: emailRow,
      priority: data.priority || 'low',
      status: data.status || 'prospect',
      notes: data.notes || '',
      address: data.address || '',
      postalCode: data.postalCode || '',
      city: data.city || '',
      website: data.website || '',
      ape: data.ape || '',
      siret: data.siret || '',
      tags,
      callbackAt: data.callbackAt || null,
    },
  };
}

export function findPreviewImportRow(rows, mapping, tagsForAll, requiredFields = []) {
  for (const row of rows) {
    const r = applyImportMappingToRow(row, mapping, tagsForAll, requiredFields);
    if (r.ok) return r.prospect;
  }
  return null;
}

export function countRowsNotImportable(rows, mapping, tagsForAll = [], requiredFields = []) {
  let n = 0;
  for (const row of rows) {
    const r = applyImportMappingToRow(row, mapping, tagsForAll, requiredFields);
    if (!r.ok) n++;
  }
  return n;
}
